// Storm-motion vector — pure, RN-portable. Parses the NWS `eventMotionDescription`
// CAP parameter that warnings carry, e.g.:
//   "2026-06-22T03:34:00-00:00...storm...278DEG...40KT...37.31,-99.58 37.0,-99.72"
//
// Convention (NWS Directive 10-512, the TIME…MOT…LOC line): the degrees value is the
// direction the storm is moving *from* — like a wind direction. So 278° = coming from
// the WNW, i.e. travelling *toward* 98° (ESE). We expose both so callers don't re-derive.

import type { NwsParameters } from '@/lib/nws/types'
import type { LatLon } from './geo'

export interface StormMotion {
  /** Direction the storm is moving FROM (degrees, meteorological). */
  fromDeg: number
  /** Direction the storm is moving TOWARD (degrees) = (fromDeg + 180) mod 360. */
  towardDeg: number
  speedKt: number
  speedMph: number
  /** Storm location point(s) at issue time (cell center, or the ends of a line). */
  points: LatLon[]
  /** ISO timestamp the vector was valid. */
  at: string | null
}

const KT_TO_MPH = 1.150779

/** Parse the storm-motion vector, or null when the warning carries none. */
export function parseMotion(parameters: NwsParameters | undefined): StormMotion | null {
  const raw = parameters?.eventMotionDescription?.[0]
  if (!raw) return null

  const deg = /(\d{1,3})\s*DEG/i.exec(raw)
  const kt = /(\d{1,3})\s*KT/i.exec(raw)
  if (!deg || !kt) return null

  const fromDeg = Number(deg[1]) % 360
  const speedKt = Number(kt[1])

  // Coordinates are whatever trails the "…KT…" segment: "lat,lon[ lat,lon …]".
  const tail = raw.slice((kt.index ?? 0) + kt[0].length)
  const points: LatLon[] = []
  for (const tok of tail.split(/\s+/)) {
    const m = /^\.*(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/.exec(tok.trim())
    if (m) points.push({ lat: Number(m[1]), lon: Number(m[2]) })
  }

  const at = /^([0-9T:+\-.Z]+)\.\.\./.exec(raw)?.[1] ?? null

  return {
    fromDeg,
    towardDeg: (fromDeg + 180) % 360,
    speedKt,
    speedMph: Math.round(speedKt * KT_TO_MPH),
    points,
    at,
  }
}

/**
 * Move the storm's location points forward along its track by `ms` of travel — so an
 * ETA recomputed minutes after issue reflects where the cell actually is now. Pure.
 */
export function advanceMotion(m: StormMotion, ms: number): StormMotion {
  if (ms <= 0 || m.speedKt <= 0 || !m.points.length) return m
  const distM = m.speedKt * 0.514444 * (ms / 1000)
  const rad = (m.towardDeg * Math.PI) / 180
  const dNorth = Math.cos(rad) * distM
  const dEast = Math.sin(rad) * distM
  const points = m.points.map((p) => {
    const lat = p.lat + dNorth / 111_320
    const lon = p.lon + dEast / (111_320 * Math.cos((p.lat * Math.PI) / 180))
    return { lat, lon }
  })
  return { ...m, points }
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']

/** Compass label for a bearing, e.g. 98 → "E". */
export function compass(deg: number): string {
  return COMPASS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]
}
